import PropTypes from "prop-types";
import React, { useState } from "react";
import { Row, Col, Alert, Card, CardBody, Container, FormFeedback, Input, Label, Form } from "reactstrap";

import { Link } from "react-router-dom";
import withRouter from "../../components/Common/withRouter";
import { withTranslation } from "react-i18next";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

// real backend client (HttpOnly-cookie aware axios instance)
import api from "../../helpers/api";

// import images
import profile from "../../assets/images/auth-bg-1.jpg";
import logo from "../../assets/images/logo-sm.png";

const ForgetPasswordPage = props => {
  const { t } = props;
  document.title = t('shared.forget_password', "Forget Password") + " | Arcmedix";

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t('shared.invalid_email', "Please enter a valid email address"))
        .required(t('shared.please_enter_email', "Please Enter Your Email")),
    }),
    onSubmit: async (values) => {
      setSubmitting(true);
      setErrorMsg("");
      setSuccessMsg("");
      try {
        await api.post('auth/forgot-password', { email: values.email.trim() });
        // Anti-enumeration: always show the same generic confirmation, whether or
        // not the email maps to an account.
        setSuccessMsg(t('shared.password_reset_link_sent',
          "If an account exists for this email, a password reset link has been sent. Please check your inbox."));
      } catch (err) {
        if (err.response?.status === 429) {
          setErrorMsg(t('shared.too_many_requests', "Too many requests. Please try again later."));
        } else {
          setErrorMsg(t('shared.something_went_wrong', "Something went wrong!"));
        }
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <React.Fragment>

      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary bg-primary-subtle">
                  <Row>
                    <Col xs={7}>
                      <div className="text-primary p-4">
                        <h5 className="text-primary">{t('shared.welcome_back', "Welcome Back !")}</h5>
                        <p>{t('shared.sign_in_to_continue', "Sign in to continue to Arcmedix")}</p>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img src={profile} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div>
                    <Link to="/">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img
                            src={logo}
                            alt=""
                            className="rounded-circle"
                            height="34"
                          />
                        </span>
                      </div>
                    </Link>
                  </div>
                  <div className="p-2">
                    {errorMsg ? (
                      <Alert color="danger" style={{ marginTop: "13px" }}>
                        {errorMsg}
                      </Alert>
                    ) : null}
                    {successMsg ? (
                      <Alert color="success" style={{ marginTop: "13px" }}>
                        {successMsg}
                      </Alert>
                    ) : null}

                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="mb-3">
                        <Label className="form-label">{t('shared.email', "Email")}</Label>
                        <Input
                          name="email"
                          className="form-control"
                          placeholder={t('shared.enter_email', "Enter email")}
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email && validation.errors.email ? true : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid"><div>{validation.errors.email}</div></FormFeedback>
                        ) : null}
                      </div>
                      <Row className="mb-3">
                        <Col className="text-end">
                          <button
                            className="btn btn-primary w-md "
                            type="submit"
                            disabled={submitting}
                          >
                            {submitting ? t('shared.please_wait', "Please wait...") : t('shared.reset', "Reset")}
                          </button>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  {t('shared.go_back_to', "Go back to")}{" "}
                  <Link to="login" className="font-weight-medium text-primary">
                    {t('shared.login', "Login")}
                  </Link>{" "}
                </p>
                <p className="text-white-50">
                  {t('shared.copyright', 'Copyright ©')} {new Date().getFullYear()} {t('shared.arcmedix_all_rights_reserved', 'Arcmedix. All rights reserved')}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

ForgetPasswordPage.propTypes = {
  history: PropTypes.object,
  t: PropTypes.func,
};

export default withRouter(withTranslation()(ForgetPasswordPage));
